import React from "react";

import { Chart } from "../../components/charts";
import { PerformanceRangeSelector } from "../single-asset-agent-dev/SingleAssetPrototypeResult";
import resultStyles from "../single-asset-agent-dev/singleAssetResult.module.css";
import { tagChartOption, tagViewSummary } from "./tagChartOptions";
import styles from "./tagPerformanceCard.module.css";

const TABS = [["ret", "收益率"], ["dd", "回撤"], ["value", "市值与投入"]];
const RANGE_LABELS = { holding: "持有期", "1m": "1月", "3m": "3月", "6m": "6月", ytd: "年初至今", "1y": "1年", "5y": "5年", all: "全部", custom: "自定义区间" };

function pctText(value) {
  return Number.isFinite(value) ? `${value > 0 ? "+" : ""}${(value * 100).toFixed(2)}%` : "--";
}

function tone(value) {
  if (!Number.isFinite(value) || value === 0) return "";
  return value > 0 ? resultStyles.gain : resultStyles.loss;
}

// 「组合表现」卡片（原型 v23）：标题 → 图表类型 + 沪深300 开关 → 图例与区间摘要 → 图 → 底部区间按钮 / 滑块。
// series 是标签汇总接口的逐日点，benchmark 是 [{date, value}] 的沪深300点位（取不到就只画标签线）。
export function TagPerformanceCard({ series, benchmark = [] }) {
  const firstDate = series[0]?.date ?? "";
  // 可选的日子 = 组合有数据的日子 ∪ 沪深300 有数据的日子：区间可以往买入前拖，买入前只有沪深300 在动。
  const dates = React.useMemo(
    () => [...new Set([...series.map((point) => point.date), ...benchmark.map((point) => point.date)])].filter(Boolean).sort(),
    [series, benchmark],
  );
  const [view, setView] = React.useState("ret");
  const [showBenchmark, setShowBenchmark] = React.useState(true);
  // 默认还是从首笔交易日（持有期）看起，往前拖才看得到买入前
  const [range, setRange] = React.useState(() => ({ key: "holding", startDate: firstDate, endDate: series.at(-1)?.date ?? "" }));
  const rangedDates = React.useMemo(
    () => dates.filter((date) => (!range.startDate || date >= range.startDate) && (!range.endDate || date <= range.endDate)),
    [dates, range],
  );
  const hasBenchmark = benchmark.length > 0;
  const benchmarkOn = view !== "value" && hasBenchmark && showBenchmark;
  const activeBenchmark = benchmarkOn ? benchmark : [];
  const option = React.useMemo(() => tagChartOption(series, view, activeBenchmark, rangedDates), [series, view, activeBenchmark, rangedDates]);
  const summary = React.useMemo(() => tagViewSummary(series, view, activeBenchmark, rangedDates), [series, view, activeBenchmark, rangedDates]);
  const rangeLabel = `${RANGE_LABELS[range.key] ?? ""}（${rangedDates[0] ?? range.startDate} 起）`;

  return (
    <section className={styles.card} aria-label="组合表现">
      <div className={styles.head}><h2>组合表现</h2></div>
      <div className={resultStyles.chartControls}>
        {TABS.map(([key, label]) => (
          <button key={key} type="button" className={resultStyles.toggle} aria-pressed={view === key} onClick={() => setView(key)}>{label}</button>
        ))}
        {view !== "value" && hasBenchmark && (
          <>
            <span className={styles.divider} aria-hidden="true" />
            <button type="button" className={resultStyles.toggle} aria-pressed={showBenchmark} onClick={() => setShowBenchmark((on) => !on)}>沪深300</button>
          </>
        )}
      </div>
      <div className={resultStyles.chartLegend} aria-live="polite">
        {view === "ret" && (
          <>
            <span><i style={{ backgroundColor: "#354052" }} />组合收益率</span>
            {benchmarkOn && <span><i className={resultStyles.legendHs300} />沪深300</span>}
            <small>
              {rangeLabel}：组合 <b className={tone(summary.own)}>{pctText(summary.own)}</b>
              {benchmarkOn && <> · 沪深300 <b className={tone(summary.bench)}>{pctText(summary.bench)}</b> · 超额 <b className={tone(summary.excess)}>{pctText(summary.excess)}</b></>}
            </small>
          </>
        )}
        {view === "dd" && (
          <>
            <span><i style={{ backgroundColor: "#354052" }} />组合回撤</span>
            {benchmarkOn && <span><i className={resultStyles.legendHs300} />沪深300回撤</span>}
            <small>
              {rangeLabel}：最大回撤 <b className={resultStyles.loss}>{pctText(summary.maxDrawdown)}</b>{summary.maxDrawdownDate ? `（${summary.maxDrawdownDate}）` : ""}
              {" "}· 当前回撤 <b className={tone(summary.currentDrawdown)}>{pctText(summary.currentDrawdown)}</b>
              {benchmarkOn && <> · 沪深300 最大回撤 <b className={resultStyles.loss}>{pctText(summary.benchMaxDrawdown)}</b></>}
            </small>
          </>
        )}
        {view === "value" && (
          <>
            <span><i style={{ backgroundColor: "#354052" }} />持仓市值</span>
            <span><i style={{ backgroundColor: "#c77914" }} />投入本金（买入抬升、卖出回落）</span>
            <small>两线之间的距离 = 浮动盈亏</small>
          </>
        )}
      </div>
      {option
        ? <div className={styles.chartWrap}><Chart option={option} /></div>
        : <div className={styles.empty}>当前时间范围没有可展示的数据。</div>}
      <PerformanceRangeSelector dates={dates} holdingStart={firstDate} range={range} setRange={setRange} />
    </section>
  );
}
